import React, { useState, useEffect, useCallback } from 'react';
import useUrlState from '../../hooks/useUrlState';
import { useAuth } from '../../auth/AuthProvider';
import { useToast } from '../../components/Toast';
import { useFirmalar } from '../../hooks/useFirmalar';
import Header from '../../components/Header';
import ErrorBanner from '../../components/ErrorBanner';
import KpiCard from '../../components/KpiCard';
import { styles, colors, radius, shadows } from '../../styles/theme';
import { formatTRY, formatSayi } from '../../utils/analytics/format';
import { exportCsv } from '../../utils/analytics/export';
import { logError } from '../../utils/logError';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const getAuthHeaders = () => {
  const token = localStorage.getItem('access_token');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

// Durum → renk/etiket (badge). Backend durum kümesi:
// GECTI, KIRMIZI, SARI, YESIL, BILINMIYOR
const DURUM_META = {
  GECTI: { label: 'GEÇTİ', bg: colors.dangerLight, color: '#991b1b' },
  KIRMIZI: { label: 'KIRMIZI', bg: colors.dangerLight, color: '#b91c1c' },
  SARI: { label: 'SARI', bg: colors.warningLight, color: '#92400e' },
  YESIL: { label: 'YEŞİL', bg: colors.successLight, color: '#166534' },
  BILINMIYOR: { label: 'BİLİNMİYOR', bg: '#f1f5f9', color: '#475569' },
};

const DurumBadge = ({ durum }) => {
  const m = DURUM_META[durum] || DURUM_META.BILINMIYOR;
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: radius.sm,
      fontSize: '11px', fontWeight: '700', background: m.bg, color: m.color,
      whiteSpace: 'nowrap',
    }}>
      {m.label}
    </span>
  );
};

const SktBozulmaPage = () => {
  useAuth();
  const toast = useToast();
  const { data: firmalar } = useFirmalar();

  const [data, setData] = useState(null);
  const [lokasyonlar, setLokasyonlar] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filtreler — URL'e bağlı (navigasyon + refresh sonrası korunur).
  const [durum, setDurum] = useUrlState('durum', '');
  const [firma, setFirma] = useUrlState('firma', '');
  const [lokasyonId, setLokasyonId] = useUrlState('lokasyon', '');

  const fetchRapor = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (durum) params.set('durum', durum);
      if (firma) params.set('firma', firma);
      if (lokasyonId) params.set('lokasyon_id', lokasyonId);
      const qs = params.toString();
      const url = `${API_URL}/api/raporlar/skt-bozulma${qs ? `?${qs}` : ''}`;

      const res = await fetch(url, { headers: getAuthHeaders() });
      if (res.ok) {
        setData(await res.json());
      } else {
        const e = await res.json().catch(() => ({}));
        const msg = e.detail || 'Rapor yüklenemedi';
        setError(msg);
        toast.error(msg);
      }
    } catch (err) {
      logError('SKT/Bozulma raporu yüklenemedi:', err);
      const msg = 'Sunucu hatası — bağlantıyı kontrol edin';
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
    // toast referansı sabit (ref tabanlı) — bağımlılık eklemeye gerek yok
  }, [durum, firma, lokasyonId, toast]);

  useEffect(() => {
    fetch(`${API_URL}/api/lokasyonlar`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : [])
      .then(d => setLokasyonlar(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetchRapor();
  }, [fetchRapor]);

  const ozet = data?.ozet || {};
  const satirlar = data?.satirlar || [];

  const exportCSV = () => {
    if (!satirlar.length) return;
    exportCsv(satirlar.map(s => ({
      Kod: s.kod || '',
      Malzeme: s.ad || '',
      Lokasyon: s.lokasyon_ad || '',
      Lot: s.lot_no || '',
      Miktar: s.miktar,
      SKT: s.skt || '',
      'SKT Türetilmiş': s.skt_turetilmis ? 'Evet' : 'Hayır',
      'Kala (gün)': s.kala_gun,
      'Değer (TL)': s.deger,
      Durum: s.durum || '',
      Kaynak: s.kaynak || '',
      Öneri: s.oneri || '',
    })), `skt-bozulma-${new Date().toISOString().split('T')[0]}.csv`);
  };

  return (
    <div>
      <Header title="SKT / Bozulma Raporu" />
      <div style={{ padding: '20px', background: colors.background, minHeight: 'calc(100vh - 60px)' }}>

        {/* Filtre Bar */}
        <div style={{ background: colors.surface, borderRadius: radius.lg, padding: '16px 20px', boxShadow: shadows.md, marginBottom: '20px' }}>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ fontSize: '13px', fontWeight: '600', color: '#555', marginRight: '8px' }}>Durum:</span>
            <select value={durum} onChange={e => setDurum(e.target.value)}
              style={{ padding: '5px 8px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: '12px', background: colors.surface }}>
              <option value="">Tümü</option>
              <option value="GECTI">GEÇTİ</option>
              <option value="KIRMIZI">KIRMIZI</option>
              <option value="SARI">SARI</option>
              <option value="YESIL">YEŞİL</option>
            </select>

            <select value={firma} onChange={e => setFirma(e.target.value)}
              style={{ padding: '5px 8px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: '12px', background: colors.surface }}>
              <option value="">Tüm Firmalar</option>
              {(firmalar || []).map(f => <option key={f.kod} value={f.kod}>{f.ad}</option>)}
            </select>

            <select value={lokasyonId} onChange={e => setLokasyonId(e.target.value)}
              style={{ padding: '5px 8px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: '12px', background: colors.surface }}>
              <option value="">Tüm Lokasyonlar</option>
              {lokasyonlar.map(l => <option key={l.id} value={l.id}>{l.ad}</option>)}
            </select>
          </div>
        </div>

        <ErrorBanner error={error} onRetry={fetchRapor} />

        {loading && !data ? (
          <div style={{ textAlign: 'center', padding: '60px', color: colors.textTertiary }}>Yükleniyor...</div>
        ) : !data ? (
          <div style={{ textAlign: 'center', padding: '60px', color: colors.textTertiary }}>Veri bulunamadı</div>
        ) : (
          <>
            {/* KPI Cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '20px' }}>
              <KpiCard title="Geçmiş (SKT Geçti)" value={ozet.gecti_adet || 0} color={styles.danger} icon="⛔" />
              <KpiCard title="Kırmızı (Kritik)" value={ozet.kirmizi_adet || 0} color="#ef5350" icon="🔴" />
              <KpiCard title="Sarı (Yaklaşan)" value={ozet.sari_adet || 0} color={styles.warning} icon="🟡" />
              <KpiCard title="Riskli Sermaye" value={formatTRY(ozet.riskli_deger || 0)} color={styles.info} icon="💰" />
            </div>

            {/* Detay Tablosu */}
            <div style={{ background: colors.surface, borderRadius: radius.lg, padding: '20px', boxShadow: shadows.md }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h3 style={{ margin: 0, fontSize: '15px', color: '#333' }}>Detay ({satirlar.length} kayit)</h3>
                <button onClick={exportCSV} disabled={!satirlar.length} style={{
                  padding: '6px 14px', border: `1px solid ${styles.primary}`, borderRadius: radius.sm,
                  background: colors.surface, color: styles.primary, cursor: satirlar.length ? 'pointer' : 'not-allowed',
                  fontSize: '12px', fontWeight: '600', opacity: satirlar.length ? 1 : 0.5,
                }}>CSV Indir</button>
              </div>

              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                  <thead>
                    <tr style={{ background: colors.backgroundAlt, borderBottom: `2px solid ${colors.border}` }}>
                      {['Malzeme', 'Lokasyon', 'Lot', 'Miktar', 'SKT', 'Kala (gün)', 'Değer (TL)', 'Durum', 'Öneri'].map(h => (
                        <th key={h} style={{ padding: '10px 8px', textAlign: 'left', fontWeight: '600', color: '#555', whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {satirlar.map((s, idx) => (
                      <tr key={idx} style={{ borderBottom: '1px solid #f0f0f0' }}>
                        <td style={{ padding: '8px' }}>
                          <span style={{ fontWeight: '500' }}>{s.ad}</span>
                          {s.kod && <span style={{ color: '#888', fontSize: '12px' }}> ({s.kod})</span>}
                        </td>
                        <td style={{ padding: '8px' }}>{s.lokasyon_ad || '-'}</td>
                        <td style={{ padding: '8px', color: '#888', fontSize: '12px' }}>{s.lot_no || '-'}</td>
                        <td style={{ padding: '8px', fontWeight: '600' }}>{formatSayi(s.miktar)}</td>
                        <td style={{ padding: '8px', whiteSpace: 'nowrap' }}>
                          {s.skt || '-'}
                          {s.skt_turetilmis && (
                            <span
                              title="SKT girilmemiş — raf ömründen tahmin edildi"
                              style={{
                                display: 'inline-block', marginLeft: '6px', padding: '1px 5px', borderRadius: radius.sm,
                                fontSize: '10px', fontWeight: '600', background: '#f1f5f9', color: '#64748b',
                              }}
                            >türetilmiş</span>
                          )}
                        </td>
                        <td style={{ padding: '8px', fontWeight: '600', color: (s.kala_gun != null && s.kala_gun < 0) ? styles.danger : '#333' }}>
                          {s.kala_gun != null ? formatSayi(s.kala_gun, { decimals: 0 }) : '-'}
                        </td>
                        <td style={{ padding: '8px' }}>{formatTRY(s.deger, { decimals: 2 })}</td>
                        <td style={{ padding: '8px' }}><DurumBadge durum={s.durum} /></td>
                        <td style={{ padding: '8px', color: '#888' }}>{s.oneri || '-'}</td>
                      </tr>
                    ))}
                    {satirlar.length === 0 && (
                      <tr><td colSpan={9} style={{ padding: '20px', textAlign: 'center', color: colors.textTertiary }}>Kayit bulunamadı</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SktBozulmaPage;
